"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const utils_http_1 = require("@blockpass-org/utils-http");
const lodash_1 = __importDefault(require("lodash"));
const API = {
    currentProfile: '/api/v1/account/profile',
    loginNormal: '/api/v1/login/email',
    loginGoogle: '/api/v1/login/google',
    verify2FA: '/api/v1/login/2fa/verify',
    requestOTP: '/api/v1/login/otp',
    confirmOTP: '/api/v1/login/otp/confirm',
    requestMagicLink: '/api/v1/login/macgiclink',
    checkMagicLinkStatus: '/api/v1/login/magiclink/status',
    confirmMagicLink: '/api/v1/login/magiclink/confirm',
    renewJWT: '/api/v1/session/renew',
    isSessionExpired: '/api/v1/session/check/expired',
    setPassword: '/api/v1/account/setpassword',
    forgotPassword: '/api/v1/account/forgotpassword',
    checkForgotPasswordSession: '/api/v1/account/forgotpassword/verify',
    confirmNewPassword: '/api/v1/account/forgotpassword/confirm',
    logout: '/api/v1/logout',
    requestEnable2FA: '/api/v1/account/2fa/enable',
    confirmEnable2FA: '/api/v1/account/2fa/enable/confirm',
    disable2FA: '/api/v1/account/2fa/disable',
    bpOperatorRole: '/api/dashboard/bpOperator/roles',
    requestRecovery: '/api/v1/recovery/request',
    verify2FARecovery: '/api/v1/recovery/2fa/verify',
    confirmRecovery: '/api/v1/recovery/confirm',
};
const HOST = {
    local: 'http://localhost:3000',
    qa: 'https://qa-oauth.blockpass.org',
    staging: 'https://sandbox-oauth.blockpass.org',
    production: 'https://oauth.blockpass.org',
    prod: 'https://oauth.blockpass.org',
};
class BpAuthClientError extends Error {
    constructor(message, code, needCaptcha = false) {
        super(message);
        this.name = 'BpAuth_Client_Error';
        this.code = code;
        this.needCaptcha = needCaptcha;
    }
}
class AuthClient {
    constructor(bpService, clientOS, clientVersion, env) {
        this.bpService = bpService;
        this.clientOS = clientOS;
        this.clientVersion = clientVersion;
        this.bpAuthHost = HOST[env];
        this.httpClient = new utils_http_1.HttpClient(bpService, clientOS, clientVersion);
        this.httpClient.setBaseUrl(this.bpAuthHost);
    }
    handleError(error) {
        if (error.isAxiosError) {
            throw new BpAuthClientError(lodash_1.default.get(error, 'response.data.error.message', 'Unknown'), lodash_1.default.get(error, 'response.status', 400), lodash_1.default.get(error, 'response.data.error.needCaptcha', false));
        }
        else {
            throw error;
        }
    }
    handleSessionResponse(data) {
        if (this.bpService == utils_http_1.BLOCKPASS_SERVICE.ma) {
            const responseData = {
                jwtSession: data.accessToken,
                profile: data.profile,
                expiresAt: data.expiresAt,
            };
            if (data.deviceId) {
                responseData.deviceId = data.deviceId;
            }
            return responseData;
        }
        else {
            return {
                session: data.accessToken,
                profile: data.profile,
                expiresAt: data.expiresAt,
            };
        }
    }
    /**
     * Function to renew jwt token. In case token is not expired return the input token.
     * @param {string} jwtToken JWT session issued by Blockpass Auth
     * @returns {Promise} Resolve to JWT Session Data
     */
    async renewJWTSession(jwtToken) {
        try {
            const res = await this.httpClient.get(API.renewJWT, {
                headers: {
                    authorization: jwtToken,
                },
            });
            const resBody = res.data;
            const responseData = {
                jwtSession: resBody.data.session,
                expiresAt: resBody.data.expiresAt,
            };
            if (resBody.data.deviceId) {
                responseData.deviceId = resBody.data.deviceId;
            }
            return responseData;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to login with Blockpass Auth using email and password. In case user activated Two FA, this function will return the verification session for Two FA.
     * @param {string} email User email
     * @param {string} password User raw password
     * @returns {Promise} Resolve to a Two Factor Verify Session or Session Data or JWT Session Data
     */
    async loginPassword(email, password, deviceId, captchaResponse) {
        try {
            const res = await this.httpClient.post(API.loginNormal, {
                email,
                password,
                deviceId,
                captchaResponse
            }, {
                withCredentials: true,
            });
            const resBody = res.data;
            if (resBody.data.session) {
                return {
                    needTwoFactor: true,
                    session: resBody.data.session,
                    expiresAfter: resBody.data.expiresAfter,
                };
            }
            else {
                return this.handleSessionResponse(resBody.data);
            }
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to login with Blockpass Auth using google login token
     * @param {string} googleToken Token Id returned from google login sdk
     * @returns {Promise} Resolve to a Two Factor Verify Session or Session Data or JWT Session Data
     */
    async loginGoogle(googleToken) {
        try {
            const res = await this.httpClient.post(API.loginGoogle, {
                tokenId: googleToken,
            }, {
                withCredentials: true,
            });
            const resBody = res.data;
            if (resBody.data.session) {
                return {
                    needTwoFactor: true,
                    session: resBody.data.session,
                    expiresAfter: resBody.data.expiresAfter,
                };
            }
            else {
                return this.handleSessionResponse(resBody.data);
            }
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to verify Two FA code from the Authenticator App
     * @param {string} code Code generate from Authenticator App
     * @param {string} session Two FA Verification Session received from Blockpass Auth
     * @param {string} deviceId Current user deviceId
     * @returns {Promise} Resolve to a Session Data
     */
    async verifyTwoFA(session, code, deviceId) {
        try {
            const res = await this.httpClient.post(API.verify2FA, {
                code,
                session,
                deviceId,
            }, { withCredentials: true });
            const resBody = res.data;
            return this.handleSessionResponse(resBody.data);
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Request Blockpass Auth Sending an OTP to begin login or registration process
     * @param {string} email User email
     * @param {string} turnstileToken cloudflare turnstile token (only using for KYB service)
     * @returns {Promise} Resolve to an OTP verification session
     */
    async requestOTP(email, turnstileToken) {
        try {
            const res = await this.httpClient.post(API.requestOTP, {
                email,
                turnstileToken,
            });
            const resBody = res.data;
            return {
                session: resBody.data.session,
                expiresAfter: resBody.data.expiresAfter,
            };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to verify OTP code sent to user by email.
     * @param {string} session OTP verification session
     * @param {string} otp OTP code
     * @param {Boolean} isRecovery flag to mark user in recover profile
     * @returns {Promise} Resolve to Session Data or JWT Session Data
     */
    async confirmOTP(session, otp, isRecovery = false) {
        try {
            const res = await this.httpClient.post(API.confirmOTP, {
                session,
                otp,
                isRecovery,
            }, { withCredentials: true });
            const resBody = res.data;
            const data = this.handleSessionResponse(resBody.data);
            data.needSetPass = resBody.data.needSetPass;
            return data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to request an magic link for login/regigstraion/switch session process.
     * @param {string} email User email
     * @param {string} redirectLink Link to redirect after verification success. This link must belong to blockpass host
     * @param {boolean} isSwitchSession Flag to mark this is a request to switch device
     * @returns {Promise} Resolve to a token to check the process status and the expire time of the verification session
     */
    async requestMagicLink(email, redirectLink, isSwitchSession = false) {
        try {
            const res = await this.httpClient.post(API.requestMagicLink, {
                email,
                redirectLink,
                isSwitchSession,
            });
            const resBody = res.data;
            return {
                statusCheckToken: resBody.data.statusCheckToken,
                expiresAt: resBody.data.expireAt,
                expiresAfter: resBody.data.expireAfter,
            };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to check magic link verification process status
     * @param {string} statusCheckToken
     * @returns {Promise} Resolve to the magic link verification process status (created or completed)
     */
    async checkMagicLink(statusCheckToken) {
        try {
            const res = await this.httpClient.get(API.checkMagicLinkStatus, {
                params: { statusCheckToken },
            });
            const resBody = res.data;
            return {
                status: resBody.data.status,
            };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to finish the magic link verification processs.
     * @param {string} token Verification Token get from the received magic link query pararm
     * @returns {Promise} Resolve to Session Data or JWT Session Data
     */
    async confirmMagicLink(token) {
        try {
            const res = await this.httpClient.post(API.confirmMagicLink, {
                token,
            }, { withCredentials: true });
            const resBody = res.data;
            return this.handleSessionResponse(resBody.data);
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to check a Raw String/JWT Session expired or not using Auth API
     * @param {string} session Raw or JWT Session
     * @returns {Promise} Resolve to true if session is expired or wrong format
     */
    async isSessionExpired(session) {
        try {
            const res = await this.httpClient.get(API.isSessionExpired, {
                headers: { authorization: session },
            });
            return lodash_1.default.get(res.data, 'data.isExpired', true);
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * (Recommended) Function to check a JWT Session expired or not locally (Not send request to Blockpass Auth)
     * @param {string} token JWT Token
     * @returns {boolean} Return true in case token is expired or wrong format
     */
    isJWTExpiredOffline(token) {
        try {
            const tokenBody = token.split('.')[1];
            if (!tokenBody) {
                return true;
            }
            let decoded = Buffer.from(tokenBody, 'base64').toString('utf-8');
            if (!decoded) {
                return true;
            }
            decoded = JSON.parse(decoded);
            // @ts-expect-error exp is in decoded but can not recognize by type
            const exp = new Date(decoded.exp * 1000).getTime();
            const curr = Date.now();
            return curr >= exp;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to set password of user. This function will throw error if user have set password
     * @param {string} session Login session
     * @param {string} password New password to set (in raw format)
     * @returns {Promise} Resolve to an object with property status success
     */
    async setPassword(session, password) {
        try {
            const res = await this.httpClient.post(API.setPassword, { password }, { headers: { authorization: session } });
            return { status: res.data.status };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to start reset password proccess.
     * @param {string} email User email
     * @param {string} redirectLink Link to redirect (WebId page or Auth page)
     * @returns {Promise} Resolve to an object with property expiresAfter (num of seconds till session expire)
     */
    async requestForgotPasswordInstruction(email, redirectLink) {
        try {
            const res = await this.httpClient.post(API.forgotPassword, {
                email,
                redirectLink,
            });
            const resBody = res.data;
            return { expiresAfter: resBody.data.expiresAfter };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to check the forgot password session status
     * @param {string} session Forgot password session get from the the url
     * @returns {Promise} Resolve to object contain expiresAfter and required2FA flag
     */
    async checkForgotPasswordSession(session) {
        try {
            const res = await this.httpClient.get(API.checkForgotPasswordSession, {
                params: { session },
            });
            const resBody = res.data;
            return {
                expiresAt: resBody.data.expiresAt,
                required2FA: resBody.data.required2FA,
            };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to set new password in forgot password process
     * @param {string} session Forgot password process session
     * @param {string} password New password (in raw format)
     * @param {string} authenticateCode Code from authenticator app (in case user actived Two FA)
     * @param {boolean} newDevice Request forgot password from new device or old device
     * @param {string} deviceId Current deviceId of user, may be null if user can not get it
     * @returns {Promise} Resolve to an object with property status success
     */
    async confirmNewPassword(session, password, authenticateCode = '', newDevice = false, deviceId = '') {
        try {
            const res = await this.httpClient.post(API.confirmNewPassword, {
                session,
                password,
                authenticateCode,
                deviceId,
                newDevice,
            }, { withCredentials: true });
            const resBody = res.data;
            if (resBody.data.backupFileDecryptionData) {
                const responseData = {
                    session: resBody.data.session,
                    expiresAfter: resBody.data.expiresAfter,
                    backupFileDecryptionData: resBody.data.backupFileDecryptionData,
                };
                return responseData;
            }
            return this.handleSessionResponse(resBody.data);
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to logout (invalidate a session)
     * @param {string} session Login session
     * @returns {Promise} Resolve to an object with property status success
     */
    async logout(session) {
        try {
            const res = await this.httpClient.post(API.logout, {}, { headers: { authorization: session } });
            return { status: res.data.status };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to request enable 2FA config
     * @param {string} session Login session
     * @param {string} password User raw password
     * @returns {Promise} Resolve to an object contain the QR code content and a secret key for manual input
     */
    async requestEnableTwoFactor(session, password) {
        try {
            const res = await this.httpClient.post(API.requestEnable2FA, { password }, { headers: { authorization: session } });
            const resBody = res.data;
            return {
                qrCodeContent: resBody.data.urlGenerate,
                secretKey: resBody.data.secret,
            };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to confirm enabling two factor
     * @param {string} session Login Session
     * @param {string} code Code from authenticator App
     * @returns {Promise} Resolve to an object with property status success
     */
    async confirmEnableTwoFactor(session, code) {
        try {
            const res = await this.httpClient.post(API.confirmEnable2FA, { code }, { headers: { authorization: session } });
            return { status: res.data.status };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to disable two factor
     * @param {string} session Login Session
     * @returns {Promise} Resolve to an object with property status success
     */
    async disableTwoFactor(session) {
        try {
            const res = await this.httpClient.post(API.disable2FA, {}, { headers: { authorization: session } });
            return { status: res.data.status };
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to open Auth Login
     * @returns {Promise} Resolve to login sessions string
     *    */
    async openLoginWindow() {
        const that = this;
        return new Promise((resolve, reject) => {
            if (!global.window) {
                throw new Error('Browser Not Support this feature');
            }
            try {
                let isSuccess = false;
                const curr = Date.now();
                const bpAuthHost = that.bpAuthHost;
                const url = new global.URL(bpAuthHost);
                url.searchParams.append('isWindow', '1');
                url.searchParams.append('bpService', that.bpService);
                url.searchParams.append('windowOrigin', global.window.location.origin);
                url.searchParams.append('timestamp', curr.toString());
                const newWindow = global.window.open(url.toString(), 'Blockpass Auth', 'width=500,height=650');
                const checkInterval = setInterval(() => {
                    if (newWindow.closed && !isSuccess) {
                        clearInterval(checkInterval);
                        return reject(new Error('User closed window before login success'));
                    }
                }, 1000);
                function handleLoginEvent(event) {
                    if (event.origin !== bpAuthHost) {
                        return;
                    }
                    if (event.data.status == 'success') {
                        isSuccess = true;
                        global.window.removeEventListener('message', handleLoginEvent);
                        clearInterval(checkInterval);
                        setTimeout(() => {
                            newWindow.close();
                            return resolve(event.data.session);
                        }, 700);
                    }
                }
                global.window.addEventListener('message', handleLoginEvent, false);
            }
            catch (error) {
                return reject(error);
            }
        });
    }
    /**
     * Function to get user profile in blockpass auth
     * @param {string} session Login session
     * @returns {Promise} Resolve to a profile object
     */
    async getCurrentProfile(session) {
        try {
            const res = await this.httpClient.get(API.currentProfile, {
                headers: { authorization: session },
            });
            const resBody = res.data;
            return resBody.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to get bp operator tenant role
     * @param {string} session Login session
     * @param {string} userId User Id
     * @returns {Promise} Resolve to array of role data
     */
    async getBpOperatorRole(session, userId) {
        try {
            const res = await this.httpClient.get(API.bpOperatorRole, {
                headers: { authorization: session },
                params: {
                    userId,
                },
            });
            const resBody = res.data;
            return resBody.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function update bp operator role
     * @param {string} session Login Session
     * @param {string} userId User id
     * @param {string} role new role
     * @returns
     */
    async updateBpOperatorRole(session, userId, role) {
        try {
            const res = await this.httpClient.put(API.bpOperatorRole, {
                userId,
                role,
            }, { headers: { authorization: session } });
            const resBody = res.data;
            return resBody.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to request recovery account
     * @param {string} email User email
     * @param {string} password User raw password
     * @returns {Promise} Resolve to a session to verify 2FA recovery flow or confirm recovery
     */
    async requestRecovery(email, password) {
        try {
            const res = await this.httpClient.post(API.requestRecovery, {
                email,
                password,
            });
            const resBody = res.data;
            return resBody.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to verify Two FA code in recovery flow from the Authenticator App
     * @param {string} code Code generate from Authenticator App
     * @param {string} session Two FA Verification Session received from Blockpass Auth
     * @returns {Promise}  Resolve to a session to confirm recovery
     */
    async verifyTwoFARecovery(session, code) {
        try {
            const res = await this.httpClient.post(API.verify2FARecovery, {
                code,
                session,
            }, { withCredentials: true });
            const resBody = res.data;
            return resBody.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Function to confirm recovery account
     * @param {string} session Session returned from requestRecovery function or verify2FARecovery function
     * @param {string} deviceId Previous deviceId in backup file
     * @param {string} backupFileName Name of backup file
     * @returns {Promise} Resolve to JWT Session Data
     */
    async confirmRecovery(session, backupFileName, deviceId) {
        try {
            const res = await this.httpClient.post(API.confirmRecovery, {
                session,
                deviceId,
                backupFileName,
            });
            const resBody = res.data;
            return this.handleSessionResponse(resBody.data);
        }
        catch (error) {
            this.handleError(error);
        }
    }
}
exports.default = AuthClient;
